import type { RecentConversationsItem } from "@albertine/types/openapi/lmt";
import { isAfter } from "date-fns";
import type { Agent } from "../common/types/Agent";
import type { Conversation } from "../common/types/Conversation";
import type { Dictionary } from "../common/types/Dictionary";
import { unassignedAgent } from "../common/utils/agents.util";
import { showTimeComparison } from "../common/utils/time.util";
import { hoursFrom } from "./time.util";
import { fromTimestampToDate, toTimestamp } from "./timestamp.util";

export function humanReadableLastMessageByAgentAt(
    conversation: Conversation | null | undefined,
): string | null | undefined {
    const lastMessageAt = conversation?.lastMessageByAgentAt;

    if (lastMessageAt) {
        return showTimeComparison(toTimestamp(lastMessageAt).toDate(), new Date());
    }
    return null;
}

export function humanReadableLastMessageByMemberAt(
    conversation: Conversation | null | undefined,
): string | null | undefined {
    const lastMessageAt = conversation?.lastMessageByMemberAt;

    if (lastMessageAt) {
        return showTimeComparison(toTimestamp(lastMessageAt).toDate(), new Date());
    }
    return null;
}

export function lastMessageAgentFirstName(
    agents: Record<string, Agent>,
    assignee: Agent | null | undefined,
    conversation: Conversation | null | undefined,
): string | null | undefined {
    if (!assignee) return null;

    if (!conversation) return null;

    const lastMessageReplyByAgent = conversation?.lastMessageByAgentAgentId
        ? agents[conversation?.lastMessageByAgentAgentId]
        : null;

    // if assignee is same as last reply agent, consider it as null as info is already shown in the
    if (lastMessageReplyByAgent?.id === assignee?.id) return null;

    return lastMessageReplyByAgent?.firstName;
}

export function conversationAssigneeCounts(
    items: RecentConversationsItem[],
): Dictionary<number> {
    const result: Dictionary<number> = {};

    for (const item of items) {
        const assignedTo = item.member.assignedToAgentId ?? unassignedAgent.id;
        result[assignedTo] = (result[assignedTo] ?? 0) + 1;
    }

    return result;
}

export function getTotalUnreadMessages<
    T extends Pick<Conversation, "unreadMessagesCountByAgent">,
>(conversations: T[]) {
    return conversations.reduce(
        (sum, conversation) => sum + (conversation.unreadMessagesCountByAgent || 0),
        0,
    );
}

export function activeConversationsWithinHours<
    T extends Pick<Conversation, "lastMessageByMemberAt" | "lastMessageByAgentAt">,
>(hours: number, conversations: T[], now?: Date): T[] {
    const hoursBeforeNow = hoursFrom(now || new Date(), -hours);
    return conversations.filter(
        (conversation) =>
            (conversation.lastMessageByMemberAt &&
                isAfter(
                    fromTimestampToDate(conversation.lastMessageByMemberAt),
                    hoursBeforeNow,
                )) ||
            (conversation.lastMessageByAgentAt &&
                isAfter(
                    fromTimestampToDate(conversation.lastMessageByAgentAt),
                    hoursBeforeNow,
                )),
    );
}
